import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { EvincedLogo } from '@evinced-private/ui-common';

import { isDashboardEnabled } from 'src/common/helpers/ConfigurationHelpers';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { useUniversalLogin } from 'src/common/hooks/useUniversalLogin';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';
import { getHomepageRedirectToLink } from 'src/common/services/HomePageRedirectionService';
import { initSentryUser, isSentryEnabled } from 'src/common/services/SentryService';
import { useBreadcrumbStore } from 'src/common/stores/BreadcrumbStore';

import { EvBreadcrumbs } from '../ev-breadcrumbs/EvBreadcrumbs';
import { EvHeader } from '../ev-header/EvHeader';

interface IDashboardHeaderProps {
	isAuthenticatedPage: boolean;
}

export const DashboardHeader: FC<IDashboardHeaderProps> = ({
	isAuthenticatedPage
}: IDashboardHeaderProps) => {
	const [shouldShowInviteOption, setShouldShowInviteOption] = useState<boolean>(false);
	const location = useLocation();
	const { isAuthenticated } = useUniversalLogin();
	const { tenant, user } = useUserTenant();
	const { configuration, getToggle } = useConfiguration();
	const { breadcrumbRoutes } = useBreadcrumbStore();

	const logoUrl = useMemo(() => {
		if (isDashboardEnabled(configuration)) {
			return RoutesHelper.getAccessibilityDashboardPath();
		}

		return getHomepageRedirectToLink(tenant, getToggle);
	}, [configuration, tenant, getToggle]);

	useEffect(() => {
		if (!isSentryEnabled()) {
			return;
		}

		if (user && tenant) {
			initSentryUser(user, tenant);
		}
	}, [user, tenant]);

	const updateShouldShowInviteOption = useCallback((): void => {
		const isUserAuthenticated = isAuthenticated();
		if (!isAuthenticatedPage || !isUserAuthenticated) {
			setShouldShowInviteOption(false);
			return;
		}
		setShouldShowInviteOption(!!tenant);
	}, [isAuthenticated, isAuthenticatedPage, tenant]);

	useEffect(() => {
		if (shouldShowInviteOption) {
			return;
		}
		updateShouldShowInviteOption();
	}, [location, shouldShowInviteOption, updateShouldShowInviteOption]);

	return (
		<EvHeader
			isAuthenticatedPage={isAuthenticated}
			logoIcon={EvincedLogo}
			onLogoClickUrl={logoUrl}
			className="dashboard-header"
			switchButtonProps={null}
		>
			{breadcrumbRoutes.length > 0 ? <EvBreadcrumbs breadcrumbsRoutes={breadcrumbRoutes} /> : null}
		</EvHeader>
	);
};
